(ns frontend.view.modals.entity-editor.view
    (:require
     [clojure.set :as set]
     [common.entity :as entity]
     [common.features.plan :as plan]
     [common.intelligence.common :as ic]
     [common.localization :as loc]
     [common.roles :as roles]
     [common.schema.constraints :as constraints]
     [common.schema.entities.survey.survey-template :as survey-template-schema]
     [common.schema.field :as field]
     [common.schema.registry :as registry]
     [frontend.actions.entity :as entity-actions]
     [frontend.components.field-editor :as field-editor]
     [frontend.logging :as logging]
     [frontend.routing :as routing]
     [frontend.state.current-session :as session]
     [frontend.state.permissions :as f-permissions]
     [frontend.utils :as fu]
     [frontend.view.entity-viewers.utils :as entity-utils]
     [frontend.view.modals.entity-editor.events :as events]
     [frontend.view.syncing-data :as syncing]
     [frontend.zap-time :as zap-time]
     [potpuri.core :as potpuri]
     [reagent.core :as r]
     [ui.components.alert.view :as alert]
     [ui.components.button.view :as button]
     [ui.components.modal.view :as modal]))

(defonce modal-key :entity-editor-modal)

(defn entity-editor
  [state error-state
   {:keys [full-entity-schema entity-atom mandatory-fields props]}]
  (let [entity-template (some->> full-entity-schema
                                 (remove (fn [field-schema]
                                           (let [visibility (->> field-schema
                                                                 :field/default-visibility
                                                                 :editor)
                                                 required-role (->> field-schema
                                                                    :field/default-authorization
                                                                    :access/write)

                                                 ;; maybe one day email can have it's own permission config ???
                                                 hide? (when (or (= visibility :editor.type/none)
                                                                 (not (isa? roles/role-hierarchy
                                                                            @session/role
                                                                            required-role)))
                                                         :not-shown)]
                                             hide?))))
        entity-template-lookup (->> entity-template
                                    (map (fn [field-schema]
                                           [(:attribute field-schema) field-schema]))
                                    (into {}))
        {:keys [prevent-backend-update? read-only? containing-entity-id]} props
        field-count (count entity-template)]
    [:div
     {:class-name (fu/cs "entity-editor")}
     (doall
       (->>
         entity-template
         (sort-by :field/default-ord)
         (map-indexed
           (fn [idx
                {:keys [:attribute :field/aggregate-attribute :field/particular-value]
                 :as   field-schema}]
             (let [entity @entity-atom
                   ;; The parent an option-scoped field needs in order to know
                   ;; which options are legal. A saved child does not carry its
                   ;; parent (the link lives on the parent as a card-many ref)
                   ;; and a client session map has no reverse refs, so the view
                   ;; that opened this editor passes the parent in as context.
                   ;; Nothing here writes it - it only reaches the filter fns
                   ;; below, via open-search!'s ctx.
                   ;; Resolved to the entity when the session has it, not left
                   ;; as the bare id. Filters that only need the id are
                   ;; unaffected - they read it through `as-id`, which takes
                   ;; either - but one that has to look something up on the
                   ;; parent cannot do it from a number. The rotation period
                   ;; override's pickers are scoped by the survey type behind
                   ;; the profile, and silently listed every rotation period
                   ;; in the tenant while this was an id.
                   scoped-entity (cond-> entity
                                   containing-entity-id
                                   (assoc :common/containing-entity
                                          (or (session/get-entity containing-entity-id)
                                              containing-entity-id)))
                   resolve-value (fn []
                                   (if (and aggregate-attribute (fn? particular-value))
                                     (set (particular-value (get @entity-atom aggregate-attribute)
                                                            @session/entities))
                                     (get @entity-atom attribute)))
                   raw-value (resolve-value)
                ;; Use display-default for showing in UI when value is nil
                ;; This doesn't affect what gets saved - only what's displayed
                   display-default (:field/display-default field-schema)
                   value (if (and (nil? raw-value) display-default)
                           display-default
                           raw-value)]
               (when (and (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                            (or (not (fn? in-use-filter?))
                                (in-use-filter? entity
                                                @session/entity-types
                                                session/has-feature-access?
                                                session/has-entity-access?)))
                          (not (:field/secondary-field? field-schema))                                 ;; secondary fields are shown in related fields in editor
                          (not= (:editor/type field-schema) :editor.type/none)
                          (or (not= (:attribute field-schema) :common/related-entities-commitments)    ;; commitments require fundraising feature + module access
                              (and (plan/available :feature/fundraising @session/tenant @session/role)
                                   (session/has-module-access? @session/account :module/fundraising))))
                 ^{:key (str "entity-creator-editor-" attribute)}
                 [field-editor/view
                  (merge
                    field-schema
                    {:id                    (str "entity-creator-editor-" attribute)
                     :entity-types-atom     session/entity-types
                     :drag-options          {:hide-drag-area? true}
                     :state-atom            state                                                       ;; state is passed  from outside so debugging is easier
                     :value                 value
                     :containing-entity     scoped-entity
                     :read-only?            (or read-only?
                                                (session/is-read-only-user?)
                                                (not (some #{(:access/type field-schema)}
                                                           [:field.access/write :field.access/create])))
                 ;; TODO: take language from user / tenant config -> default to :en
                 ;; if only :en in field/translations use :en
                     :label                 (or (->> field-schema
                                                     :field/translations
                                                     :en)
                                                (loc/tr attribute)
                                                (when-let [fs (registry/get-field-schema
                                                                @session/tenant-schema
                                                                attribute)]
                                                  (loc/field-name attribute fs)))                       ;; we select label outside of field-editor so we can customize label if needed
                     :mandatory?            (and (not (session/is-read-only-user?))
                                                 (boolean (some #{attribute} mandatory-fields)))        ;; can be function or boolean - most likely always view dependent
                     :placeholder           (field/placeholder field-schema)
                     :close-menu-on-scroll? true
                     :menu-portal-target    (.getElementById js/document "react-select-menu-container")
                 ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
                     :tools                 [:tool-item/cancel :tool-item/clear]
                     :error-text            nil                                                         ;; TODO: use editor-atom to store validation and save errors
                     :validate!
                     (fn [updated-attribute value]
                       (prn updated-attribute attribute)
                       (let [field-constraints (when (= attribute updated-attribute)
                                                 (:field/constraints field-schema))
                             errors (constraints/validate field-constraints
                                                          (:db/id entity)
                                                          value
                                                          entity
                                                          (partial
                                                            session/unique-check
                                                            false
                                                            (partial
                                                              routing/open-entity))
                                                          loc/tr)
                         ;;skip person email validation if value is empty
                             errors (if (and (= :person/universal-email-address
                                                updated-attribute)
                                             (= "" value))
                                      nil
                                      errors)]
                         (if errors
                           (swap! error-state assoc
                                  updated-attribute errors)
                           (swap! error-state
                                  dissoc updated-attribute))

                         errors))
                     :on-save!
                     (fn [attribute editor-new-value on-success! _on-error!]
                   ;;optimistic save to frontend db:
                       (let [save-attribute (or aggregate-attribute attribute)
                             old-attribute-value (get @entity-atom save-attribute)
                             edited-value (if-not aggregate-attribute
                                            editor-new-value
                                            (let [new-value (set editor-new-value)
                                                  old-value (resolve-value)
                                                  added-values (set/difference new-value old-value)
                                                  removed-values (set/difference old-value new-value)
                                                  old-aggregate-value old-attribute-value]
                                              (-> old-aggregate-value
                                                  (set/union added-values)
                                                  (set/difference removed-values))))]
                         (if (or (= edited-value old-attribute-value)
                                 (and (fu/empty-value? edited-value)
                                      (fu/empty-value? old-attribute-value)))
                       ;; no changes ->> OK
                           (on-success!)
                           (do
                         ;; optimistic save
                             (swap! state assoc save-attribute edited-value)
                             (deref state)
                             (when-not prevent-backend-update?
                               (entity-actions/*->field-update!
                                 (:db/id entity)
                                 save-attribute
                                 edited-value
                                 (fn [_]
                              ;; save OK
                                   (on-success!))
                                 (fn [err]
                                   (let [current-attribute-value (get @entity-atom save-attribute)]
                                ;; optimistic save rollback
                                     (when (or (= edited-value current-attribute-value)
                                               (and (fu/empty-value? edited-value)
                                                    (fu/empty-value? current-attribute-value)))
                                       (swap! state assoc save-attribute old-attribute-value)
                                       (deref state))
                                     (logging/error "Entity-editor-modal : Failed to save attribute : "
                                                    err)))))))))}
                    ;; Both filter properties are read back from the registry, not
                    ;; from `field-schema`: the latter comes from the serialized
                    ;; tenant schema, which cannot carry a function (the same
                    ;; reason the :label lookup above goes back to the registry).
                    ;; Without this the editor searched and listed every option a
                    ;; field declared a filter for - the creator modal already
                    ;; forwards both; this one never did.
                    (when-let [option-filter-fn (:field/option-filter-fn
                                                 (registry/get-field-schema @session/tenant-schema
                                                                            attribute))]
                      {:field/option-filter-fn (fn [filter-args]
                                                 (option-filter-fn
                                                  (merge filter-args
                                                         {:tenant            @session/tenant
                                                          :containing-entity scoped-entity})))})
                    (when-let [os-filter-terms-fn (:field/os-filter-terms-fn
                                                   (registry/get-field-schema @session/tenant-schema
                                                                              attribute))]
                      {:field/os-filter-terms-fn (fn [search-ctx]
                                                   (os-filter-terms-fn
                                                    (merge search-ctx
                                                           {:tenant            @session/tenant
                                                            :containing-entity scoped-entity})))})
                    (when-let [create-start-value-fn (:field/create-start-value-fn
                                                      (registry/get-field-schema @session/tenant-schema
                                                                                 attribute))]
                      {:field/create-start-value-fn (fn [seed-args]
                                                      (create-start-value-fn
                                                       (merge seed-args
                                                              {:tenant            @session/tenant
                                                               :containing-entity scoped-entity})))})
                    (when (>= idx (- field-count 1))
                      {:menu-placement "top"})
                    (when-let [secondary-attribute (:field/secondary-attribute field-schema)]           ;; secondary attribute
                      {:secondary-view-args
                       (let [sec-templ (get entity-template-lookup secondary-attribute)]
                         (merge
                           sec-templ
                           {:state-atom         state
                            :value              (or (get @entity-atom secondary-attribute)
                                                    (when (= (:field/data-type sec-templ)
                                                             :data.type/currency-symbol)
                                                      (:tenant/default-currency @session/tenant)))
                            :menu-portal-target (.getElementById js/document
                                                                 "react-select-menu-container")
                            :label              (loc/tr secondary-attribute)                                 ;; when we have custom dynamic fields the labels come from tenant configuration
                            :mandatory?         (boolean (some #{attribute} mandatory-fields))
                            :error-text         nil}))}))]))))))]))

(defn editor-body
  [state error-state {:keys [:edit-template :entity-type :mandatory-fields :props]}]
  (let [hide-alert! identity]
    [:div.create-modal__body
     (when (:alert @state)
       [:div.form-group
        [alert/danger
         {:on-close hide-alert!}
         (:alert @state)]])
     [entity-editor state error-state
      {:full-entity-schema edit-template
       :entity-atom        state
       :entity-type        entity-type
       :props              props
       :mandatory-fields   mandatory-fields}]]))

(defn editor-footer
  [entity-state state _alert-state {:keys [:props :close!]}]
  (let [{:keys [on-ok! no-changes? read-only?
                ok-button-icon ok-button-text
                cancel-button-text]}
        props
        read-only? (or read-only?
                       (session/is-read-only-user?))
        no-updates? (when (fn? no-changes?)
                      (no-changes?))]
    [:<>
     [:div
      {:class "flex gap-3"}
      [button/secondary-hollow
       {:on-click close!}
       [:span
        (if read-only?
          (loc/tr :close)
          (or cancel-button-text
              (if (fn? on-ok!)
                (loc/tr :cancel)
                (loc/tr :close))))]]]
     (when (and (not read-only?)
                (fn? on-ok!))
       (let [errors (events/validate-form entity-state state)
             submit-disabled? (or no-updates?
                                  (seq errors))]
         [modal/right-single-button
          nil
          submit-disabled?
          (fn [_]
            (if on-ok!
              (on-ok! (:db/id @entity-state) close!)
              (close!)))
          [:<>
           (when ok-button-icon
             ok-button-icon)
           [:span
            (or ok-button-text
                (loc/tr :submit))]]
          nil]))]))

(defn viewer-body
  [state {:keys [props]}]
  (let [{:keys [entity-type]}
        props
        entity (get @session/entities (:db/id @state))
        enriched-entity (ic/enrich-entity entity)
        hide-keys (concat (keys entity)
                          [:person/avatar-url                                                       ;;TODO more generic way to list hidden intelligence fields
                           :person/gender
                           :company/logo])
        intelligence-data (apply dissoc enriched-entity hide-keys)]
    [:div.create-modal__body
     (if (and intelligence-data
              (seq intelligence-data))
       [:div.create-intelligence
        [:div.create-intelligence__right
         [:h2 [:span [:i.fal.fa-exclamation-circle] (loc/tr :create-entity-found-intelligence)]]
         [:h4 (loc/tr :create-entity-found-intelligence-disclaimer)]
         [:ul
          (doall (for [[k v] intelligence-data]
                   [:li {:key (str "intelligence-data-" k)}
                    (str (loc/tr k) " : " v)]))]]]
       (if (and (or (:company/website entity)
                    (:person/universal-email-address entity))
                (> (- (zap-time/now)
                      (:success-time @state))
                   5000))
         (loc/tr :enriching-data)
         [:p (loc/tr :entity-created-successfully [(loc/tr entity-type)])]))]))

(defn viewer-footer
  [{:keys [:done!]}]
  [modal/right-single-button
   nil
   false
   done!
   (loc/tr :continue)
   nil])

(def min-spinner-time-in-ms 500)

(defn resolve-raw-schema
  [entity-type schema entity]
  (case entity-type
    :entity.type/survey-template (-> schema
                                     (dissoc :survey-template/template-blocks)
                                     (assoc-in [:survey-template/show-previous-response-setting :field/display-default]
                                               survey-template-schema/show-previous-response-default))
    :entity.type/named-value (potpuri/deep-merge
                               schema
                               {:named-value/value
                                {:named-value-attribute (:named-value/attribute entity)}})
    :entity.type/company (potpuri/deep-merge
                           schema
                           {:asset/name
                            {:field/default-visibility {:editor :editor.type/text-simple}}})
    :entity.type/person schema
    :entity.type/fundraising-opportunity
    (potpuri/deep-merge
      schema
      {:fundraising-opportunity/opportunity-name {:field/default-visibility
                                                  {:editor :editor.type/text-simple}}
       :fundraising-opportunity/investor         {:field/default-visibility
                                                  {:editor :editor.type/select}}})

    :entity.type/holding
    (dissoc (potpuri/deep-merge
              schema
              {:holding/name
               {:field/default-visibility {:editor :editor.type/text-simple}}
               :holding/target-asset
               {:field/default-visibility {:editor :editor.type/select}}})
            :holding/tag-tags)

    :entity.type/oppo (dissoc schema
                              :oppo/on-hold-until)
    :entity.type/fee schema
    :entity.type/liability schema
    :entity.type/metabase-dashboard
    (dissoc schema
            :metabase-dashboard/metabase-dashboard-id
            :metabase-dashboard/url
            :metabase-dashboard/pipeline-filtering?
            :metabase-dashboard/label)
    schema))

(defn view
  [{:keys [props state] :as _modal-props}]
  (let [{:keys [entity-id mandatory-fields]} props
        entity (or (when (:entity-state props)
                     @(:entity-state props))
                   (get @session/entities entity-id))
        entity-id (:entity-id props)
        error-state (r/cursor state [:errors])
        entity-state (or (:entity-state props)
                         (r/cursor session/entities [entity-id]))
        alert-state (r/cursor state [:server-errors])
        entity-type (:entity/type entity)]
    (events/reset-state! state
                         error-state
                         alert-state
                         entity-type
                         mandatory-fields)
    (fn [{:keys [shown? close-modal! props state topmost?] :as _modal-props}]
      (let [{:keys [entity-id on-cancel! on-ok! title-text mandatory-fields read-only-fields]} props
            entity (or (when (:entity-state props)
                         @(:entity-state props))
                       (get @session/entities entity-id))
            entity-type (:entity/type entity)
            close! (fn []
                     (when on-cancel!
                       (on-cancel!))
                     (close-modal!))
            schema (registry/get-schema @session/tenant-schema entity-type)
            raw-schema (resolve-raw-schema entity-type schema entity)
            full-schema
            (->> raw-schema
                 (filter
                   (fn [[attribute _field-schema]]
                     (f-permissions/field-read-access? attribute
                                                       entity-type
                                                       (fu/pipeline-id entity))))
                 (map (fn [[attribute field-schema]]
                        (merge field-schema
                               {:attribute   attribute
                                :access/type (if (some #{attribute} read-only-fields)
                                               :field.access/read
                                               :field.access/write)
                                :editor/type (entity-utils/resolve-editor-type attribute
                                                                               field-schema)}))))
            entity-template (->> full-schema
                                 (remove (fn [field-schema]
                                           (let [visibility (->> field-schema
                                                                 :field/default-visibility
                                                                 :editor)]
                                             (when (= visibility :editor.type/none)
                                               :not-shown)))))
            finished? (and (:db/id @state)
                           (not (:processing @state)))
            done! #(if on-ok!
                     (on-ok! entity-id close-modal!)
                     (close-modal!))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         (if finished? done! close!)
          :backdrop      "static"
          :close-button? (not finished?)}
         (or title-text (entity/display-string entity))
         (cond
           (not (or (:processing @state)
                    (when (< (- (zap-time/now) (:start-time @state)) min-spinner-time-in-ms)
                      (let [since-start (-> min-spinner-time-in-ms
                                            (- (zap-time/now))
                                            (+ (:start-time @state)))
                            timeout (max 100 since-start)]
                        (js/setTimeout #(swap! state assoc :start-time 0) timeout)
                        true))))
           [editor-body entity-state error-state
            {:edit-template    entity-template
             :entity-type      entity-type
             :props            props
             :mandatory-fields mandatory-fields}]
           (or (:company/website (:draft-entity @state))
               (:person/universal-email-address (:draft-entity @state)))
           [syncing/sync-content]
           :else
           [:div.spinner-wrapper
            [:i.fal.fa-spinner.fa-pulse.fa-4x]
            [:span.processing-label (loc/tr :processing)]])
         (when-not (:processing @state)
           [editor-footer entity-state state alert-state
            {:props  props
             :close! close!}])]))))
